import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  Wallet,
  CreditCard,
  SearchX,
  SearchCheck,
  LifeBuoy,
  Bug,
  CalendarClock,
  Activity,
  Building2,
  Bot,
  BookOpen,
  Settings,
  ChevronLeft,
  ChevronRight,
  Phone,
  PhoneCall,
  Route,
  Shield,
  ShieldCheck,
  Users,
  ClipboardList,
  Layers,
  PlugZap,
  AudioLines,
  Globe2,
  ArrowRightLeft,
  Radio,
} from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { ScrollArea } from '@shared/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/components/ui/tooltip';
import { useUiStore } from '@app/store/ui.store';
import { BrandMark, BrandWordmark } from '@core/brand';
import { useAuthStore } from '@app/store/auth.store';
import { canAccessNav, type NavKey } from '@features/auth/roles';
import { Button } from '@shared/components/ui/button';
import type { AuthUser } from '@features/auth/types';

interface NavItem {
  label: string;
  icon: React.ElementType;
  to: string;
  end?: boolean;
  key: NavKey;
}

interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

/** Related menu items grouped for the left nav. */
const NAV_SECTIONS: NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      {
        label: 'Analytics',
        icon: BarChart3,
        to: '/admin/analytics',
        key: 'analytics',
      },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    items: [
      { label: 'Agents', icon: Bot, to: '/admin/agents', key: 'agents' },
      {
        label: 'Integrations',
        icon: PlugZap,
        to: '/admin/integrations',
        key: 'integrations',
      },
      {
        label: 'Knowledge Base',
        icon: BookOpen,
        to: '/admin/knowledge',
        key: 'knowledge',
      },
      {
        label: 'Phone Numbers',
        icon: PhoneCall,
        to: '/admin/phone-numbers',
        key: 'phone-numbers',
      },
      {
        label: 'Routing Policies',
        icon: Route,
        to: '/admin/routing-policies',
        key: 'routing-policies',
      },
      {
        label: 'Settings',
        icon: Settings,
        to: '/admin/settings',
        key: 'settings',
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        label: 'Knowledge Gaps',
        icon: SearchX,
        to: '/admin/knowledge-gaps',
        key: 'knowledge-gaps',
      },
      {
        label: 'KB Answers',
        icon: SearchCheck,
        to: '/admin/knowledge-answers',
        key: 'knowledge-answers',
      },
      {
        label: 'Live Calls',
        icon: Radio,
        to: '/admin/live-calls',
        key: 'live-calls',
      },
      {
        label: 'Handoffs',
        icon: LifeBuoy,
        to: '/admin/handoffs',
        key: 'handoffs',
      },
      { label: 'Tickets', icon: Bug, to: '/admin/tickets', key: 'tickets' },
      {
        label: 'Agent Actions',
        icon: Activity,
        to: '/admin/agent-actions',
        key: 'agent-actions',
      },
      {
        label: 'Meetings',
        icon: CalendarClock,
        to: '/admin/meetings',
        key: 'meetings',
      },
      {
        label: 'Call Logs',
        icon: Phone,
        to: '/admin/call-logs',
        key: 'call-logs',
      },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    items: [
      { label: 'Billing', icon: Wallet, to: '/admin/billing', key: 'billing' },
      { label: 'Plan & Billing', icon: Layers, to: '/admin/my-plan', key: 'my-plan' },
    ],
  },
  {
    id: 'access',
    label: 'Access',
    items: [
      { label: 'Users', icon: Users, to: '/admin/users', key: 'users' },
      { label: 'Roles', icon: Shield, to: '/admin/roles', key: 'roles' },
      {
        label: 'Audit Log',
        icon: ClipboardList,
        to: '/admin/audit',
        key: 'audit',
      },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    items: [
      {
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/admin',
        end: true,
        key: 'dashboard',
      },
      {
        label: 'Platform Billing',
        icon: CreditCard,
        to: '/admin/platform-billing',
        key: 'platform-billing',
      },
      {
        label: 'Plans & Pricing',
        icon: Layers,
        to: '/admin/platform-plans',
        key: 'platform-plans',
      },
      {
        label: 'Tenant Billing',
        icon: Wallet,
        to: '/admin/platform-tenant-billing',
        key: 'platform-tenant-billing',
      },
      {
        label: 'Invoices',
        icon: ClipboardList,
        to: '/admin/platform-invoices',
        key: 'platform-invoices',
      },
      {
        label: 'Tenants',
        icon: Building2,
        to: '/admin/tenants',
        key: 'tenants',
      },
      {
        label: 'Voice Engines',
        icon: AudioLines,
        to: '/admin/voice-engines',
        key: 'voice-engines',
      },
      {
        label: 'Telephony Countries',
        icon: Globe2,
        to: '/admin/telephony-countries',
        key: 'telephony-countries',
      },
      {
        label: 'Porting Requests',
        icon: ArrowRightLeft,
        to: '/admin/porting-requests',
        key: 'porting-requests',
      },
      {
        label: 'Platform Users',
        icon: Users,
        to: '/admin/platform-users',
        key: 'platform-users',
      },
      {
        label: 'Platform Roles',
        icon: ShieldCheck,
        to: '/admin/platform-roles',
        key: 'platform-roles',
      },
      {
        label: 'Platform Audit',
        icon: ClipboardList,
        to: '/admin/platform-audit',
        key: 'platform-audit',
      },
    ],
  },
];

// Profile/Security live in the header avatar menu; tenant Settings moved into
// the Workspace section — so there is no separate Account sidebar group.

function filterSection(
  section: NavSection,
  user: AuthUser | null | undefined,
): NavSection | null {
  const items = section.items.filter((item) => canAccessNav(user, item.key));
  if (items.length === 0) return null;
  return { ...section, items };
}

interface SidebarLinkProps {
  item: NavItem;
  collapsed: boolean;
}

function SidebarLink({ item, collapsed }: SidebarLinkProps) {
  const { pathname } = useLocation();
  // Match exact path, or a real child segment (`/admin/agents/123`). Do not
  // treat siblings that share a prefix as active (`/admin/knowledge` must not
  // stay selected on `/admin/knowledge-gaps` or `/admin/knowledge-answers`).
  const isActive = item.end
    ? pathname === item.to
    : pathname === item.to || pathname.startsWith(`${item.to}/`);

  const inner = (
    <NavLink
      to={item.to}
      end={item.end}
      className={cn(
        'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
        'hover:bg-accent hover:text-accent-foreground',
        isActive
          ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary hover:text-primary-foreground'
          : 'text-muted-foreground',
        collapsed && 'justify-center px-2',
      )}
    >
      <item.icon
        className={cn('shrink-0', collapsed ? 'h-5 w-5' : 'h-4 w-4')}
      />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{inner}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return inner;
}

function NavSectionBlock({
  section,
  collapsed,
  showDivider,
}: {
  section: NavSection;
  collapsed: boolean;
  showDivider: boolean;
}) {
  return (
    <div className={cn(showDivider && (collapsed ? 'mt-2 pt-2 border-t' : 'mt-3'))}>
      {!collapsed && (
        <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
          {section.label}
        </p>
      )}
      <nav className="flex flex-col gap-0.5" aria-label={section.label}>
        {section.items.map((item) => (
          <SidebarLink key={item.to} item={item} collapsed={collapsed} />
        ))}
      </nav>
    </div>
  );
}

export function AdminSidebar({
  forceExpanded = false,
}: {
  /** When true (mobile sheet), always show labels — ignore desktop collapse. */
  forceExpanded?: boolean;
}) {
  const { sidebarOpen, toggleSidebar } = useUiStore();
  const user = useAuthStore((s) => s.user);
  const collapsed = forceExpanded ? false : !sidebarOpen;

  const mainSections = NAV_SECTIONS.map((s) => filterSection(s, user)).filter(
    (s): s is NavSection => s != null,
  );

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'relative flex h-screen flex-col border-r bg-background transition-all duration-300 ease-in-out',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        {/* ── Brand ─────────────────────────────────────────────── */}
        <div
          className={cn(
            'flex h-16 shrink-0 items-center border-b px-4',
            collapsed ? 'justify-center px-2' : 'gap-2',
          )}
        >
          {collapsed ? (
            <BrandMark size="sm" />
          ) : (
            <BrandWordmark subtitle="Admin Panel" />
          )}
        </div>

        {/* ── Grouped Nav ───────────────────────────────────────── */}
        <ScrollArea className="flex-1 px-2 py-3">
          {mainSections.map((section, index) => (
            <NavSectionBlock
              key={section.id}
              section={section}
              collapsed={collapsed}
              showDivider={index > 0}
            />
          ))}
        </ScrollArea>

        {/* ── Collapse Toggle (desktop only) ─────────────────────── */}
        {!forceExpanded && (
          <div className="shrink-0 border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center text-muted-foreground hover:text-foreground"
              onClick={toggleSidebar}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <>
                  <ChevronLeft className="h-4 w-4" />
                  <span className="ml-2 text-xs">Collapse</span>
                </>
              )}
            </Button>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}
